import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo, useState } from 'react'
import { Combobox } from '@/components'
import { cn } from '@/lib/utils'
import { ALL_ISSUE_RELATIONS_QUERY } from '../queries'

interface RelationOptionNode {
  id: string
  name: string
}

interface RelationNode {
  id: string
  name: string
  required: boolean
  options: { edges: { node: RelationOptionNode }[] }
}

interface AllIssueRelationsData {
  allIssueRelations: { edges: { node: RelationNode }[] }
}

interface UseIssueRelationsResult {
  /** Relations rendered as cells (required + opted-in optionals). */
  visibleRelations: RelationNode[]
  /** Optional relations not yet added — surfaced in the "Add" picker. */
  availableOptionalRelations: RelationNode[]
  setRelationOption(relationId: string, optionId: string | null): void
  /** Reveal an optional relation as a cell. */
  addOptional(relationId: string): void
  /** Hide an optional relation and clear its value. */
  removeOptional(relationId: string): void
}

/**
 * Returns true if every required IssueRelation has an option chosen in
 * `value`. Returns true while relations are still loading so the UI does
 * not flash "invalid" before data arrives.
 */
export const useRequiredRelationsSatisfied = (
  value: Record<string, string | null>
): boolean => {
  const { data, loading } = useQuery<AllIssueRelationsData>(
    ALL_ISSUE_RELATIONS_QUERY
  )
  if (loading || !data) return true
  return data.allIssueRelations.edges
    .map(e => e.node)
    .filter(r => r.required)
    .every(r => !!value[r.id])
}

/**
 * Hook that fetches IssueRelations and returns the data + helpers needed by
 * forms.
 *
 * Required relations are always visible. Optional relations are visible only
 * when the user has explicitly added them or when an existing value is
 * present (e.g. editing an issue that already has a chosen option).
 */
export const useIssueRelations = (
  value: Record<string, string | null>,
  onChange: (next: Record<string, string | null>) => void
): UseIssueRelationsResult => {
  const { data } = useQuery<AllIssueRelationsData>(ALL_ISSUE_RELATIONS_QUERY)
  const relations = useMemo(
    () => data?.allIssueRelations.edges.map(e => e.node) ?? [],
    [data]
  )

  // Tracks which optional relations the user has explicitly revealed.
  const [addedOptionalIds, setAddedOptionalIds] = useState<Set<string>>(
    new Set()
  )

  const setRelationOption = useCallback(
    (relationId: string, optionId: string | null) => {
      onChange({ ...value, [relationId]: optionId })
    },
    [value, onChange]
  )

  const addOptional = useCallback((relationId: string) => {
    setAddedOptionalIds(prev => {
      if (prev.has(relationId)) return prev
      const next = new Set(prev)
      next.add(relationId)
      return next
    })
  }, [])

  const removeOptional = useCallback(
    (relationId: string) => {
      setAddedOptionalIds(prev => {
        if (!prev.has(relationId)) return prev
        const next = new Set(prev)
        next.delete(relationId)
        return next
      })
      if (value[relationId]) {
        onChange({ ...value, [relationId]: null })
      }
    },
    [value, onChange]
  )

  const visibleRelations = useMemo(
    () =>
      relations.filter(
        r => r.required || addedOptionalIds.has(r.id) || !!value[r.id]
      ),
    [relations, addedOptionalIds, value]
  )

  const availableOptionalRelations = useMemo(
    () =>
      relations.filter(
        r => !r.required && !addedOptionalIds.has(r.id) && !value[r.id]
      ),
    [relations, addedOptionalIds, value]
  )

  return {
    visibleRelations,
    availableOptionalRelations,
    setRelationOption,
    addOptional,
    removeOptional,
  }
}

/**
 * Render a single relation as a dropdown. Caller wraps it in a grid cell so
 * the visual style matches surrounding fields.
 *
 * The combobox is deliberately not clearable: clearing the value is done via
 * the outer remove button on optional relations, and required relations must
 * always have a value.
 */
interface RelationFieldProps {
  relation: RelationNode
  value: string | null
  onChange(optionId: string | null): void
}

export const IssueRelationField: React.FC<RelationFieldProps> = ({
  relation,
  value,
  onChange,
}) => {
  const translations = useTranslate({ select: 'common.select' })

  const items = relation.options.edges.map(e => ({
    label: e.node.name,
    value: e.node.id,
  }))

  return (
    <Combobox
      mode="single"
      value={value ?? ''}
      items={items}
      placeholder={translations.select}
      onChange={id => onChange(id)}
    />
  )
}

interface IssueRelationsPanelProps {
  /** Current form value: relationId -> selected optionId (or null). */
  value: Record<string, string | null>
  /** Render selects when true, read-only values when false. */
  editable: boolean
  onChange(relationId: string, optionId: string | null): void
  className?: string
}

/**
 * Renders every IssueRelation up-front — required and optional alike — so the
 * user can see what an issue can be linked to before linking anything. Linked
 * relations show their option; unlinked ones show an empty state (read mode)
 * or an inline select (edit mode). Replaces the old "hidden until added"
 * dropdown flow.
 */
export const IssueRelationsPanel: React.FC<IssueRelationsPanelProps> = ({
  value,
  editable,
  onChange,
  className,
}) => {
  const translations = useTranslate({
    relations: 'issues.relations',
    notLinked: 'issues.not-linked',
  })
  const { data } = useQuery<AllIssueRelationsData>(ALL_ISSUE_RELATIONS_QUERY)
  const relations = data?.allIssueRelations.edges.map(e => e.node) ?? []

  if (relations.length === 0) return null

  return (
    <section className={cn('rounded-2xl bg-white shadow-card', className)}>
      <header className="border-b border-stone-200 px-5 py-4">
        <h4 className="m-0 text-base font-semibold text-stone-900">
          {translations.relations}
        </h4>
      </header>
      <div className="divide-y divide-stone-200">
        {relations.map(relation => {
          const selectedId = value[relation.id] ?? null
          const selectedName =
            relation.options.edges.find(e => e.node.id === selectedId)?.node
              .name ?? null
          const linked = !!selectedName

          return (
            <div
              key={relation.id}
              className="flex flex-col gap-1.5 px-5 py-3.5"
            >
              <span className="flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
                {relation.name}
                {relation.required && <span className="text-rose-500">*</span>}
              </span>
              {editable ? (
                <IssueRelationField
                  relation={relation}
                  value={selectedId}
                  onChange={id => onChange(relation.id, id)}
                />
              ) : linked ? (
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-stone-300 bg-stone-100 px-3 py-1 text-sm font-semibold text-stone-800">
                  <span className="h-1.5 w-1.5 rounded-full bg-stone-500" />
                  {selectedName}
                </span>
              ) : (
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-dashed border-stone-300 px-3 py-1 text-sm text-stone-400">
                  {translations.notLinked}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

/** Build initial form value from an existing issue's relation_options edges. */
export const buildInitialRelationOptions = (
  edges?: { node: { id: string; relation: { id: string } } }[]
): Record<string, string | null> => {
  const out: Record<string, string | null> = {}
  for (const edge of edges ?? []) {
    out[edge.node.relation.id] = edge.node.id
  }
  return out
}

/** Build mutation payload: list of selected IssueRelationOption ids. */
export const serializeRelationOptions = (
  value: Record<string, string | null>
): string[] => Object.values(value).filter((v): v is string => !!v)
